import React, { useState, useMemo } from "react";
import { useVenue } from "./VenueProvider";
import { isContactFormLead } from "../utils/leadHelpers";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
  Tooltip,
} from "recharts";
import {
  ChartBarIcon,
  TableCellsIcon,
  PresentationChartLineIcon,
  InformationCircleIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  FunnelIcon,
  CursorArrowRaysIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from "@heroicons/react/20/solid";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "./table";

const ACCENT = "#1b4d2e";

// Paletas validadas con el validador de dataviz (modo claro, único del app):
// - embudo: ramp ordinal de un solo hue, claro→oscuro (etapas ordenadas)
// - plataformas: categórica semántica (IG rosa, WA verde, Web azul);
//   el rosa queda bajo 3:1 de contraste → siempre va acompañado de etiquetas
//   directas con el valor (nunca color solo)
const FUNNEL_COLORS = ["#77b28e", "#3f8f5c", "#1b4d2e"];
const PLATFORM_META = {
  ig: { label: "Instagram", color: "#e87ba4" },
  wa: { label: "WhatsApp", color: "#008300" },
  web: { label: "Sitio Web", color: "#2a78d6" },
  otro: { label: "Otro", color: "#a1a1aa" },
};
const PLATFORM_ORDER = ["ig", "wa", "web", "otro"];

const TIMEFRAMES = [
  { label: "7 días", value: 7 },
  { label: "15 días", value: 15 },
  { label: "30 días", value: 30 },
  { label: "Todo", value: "all" },
];

const DAY_MS = 24 * 60 * 60 * 1000;

// Firestore Timestamp | string | number → epoch ms (null si no se puede)
const toMs = (ts) => {
  if (!ts) return null;
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  if (typeof ts.toDate === "function") return ts.toDate().getTime();
  if (typeof ts === "string" || typeof ts === "number") {
    const ms = +new Date(ts);
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
};

const dayKey = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const monthKey = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const dayLabel = (key) =>
  new Date(`${key}T12:00:00`).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 15).toLocaleDateString("es-MX", { month: "short", year: "2-digit" });
};

const fmtPct = (v, digits = 1) => `${v.toFixed(digits)}%`;

const Section = ({ icon: Icon, title, children, className = "", action }) => (
  <div className={`rounded-xl border border-zinc-200 bg-white p-5 shadow-sm ${className}`}>
    <div className="flex items-center gap-2 mb-4">
      <div
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: ACCENT + "1a" }}
      >
        <Icon className="h-4 w-4" style={{ color: ACCENT }} />
      </div>
      <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
        {title}
      </span>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3 shadow-lg text-sm">
      <div className="font-semibold text-zinc-900 mb-1">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
          <span className="text-zinc-500">{entry.name}:</span>
          <span className="font-medium text-zinc-900">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

const InfoHint = ({ text }) => (
  <span className="group relative inline-flex">
    <InformationCircleIcon className="h-4 w-4 text-zinc-400 cursor-help" />
    <span className="pointer-events-none absolute right-0 top-6 z-10 w-52 rounded-lg bg-zinc-800 px-3 py-2 text-xs text-white shadow-lg opacity-0 transition-opacity group-hover:opacity-100">
      {text}
    </span>
  </span>
);

// Chip de variación vs. el periodo anterior de igual duración. `unit` "pp"
// para diferencias de porcentaje; para conteos muestra % de cambio.
const DeltaChip = ({ current, previous, unit = "%" }) => {
  if (previous == null || current == null) return null;
  let text;
  let up;
  if (unit === "pp") {
    const diff = current - previous;
    if (Math.abs(diff) < 0.05) return <span className="text-xs text-zinc-400">sin cambio</span>;
    up = diff > 0;
    text = `${up ? "+" : ""}${diff.toFixed(1)} pp`;
  } else {
    if (previous === 0) {
      if (current === 0) return <span className="text-xs text-zinc-400">sin cambio</span>;
      up = true;
      text = "nuevo";
    } else {
      const pct = ((current - previous) / previous) * 100;
      if (Math.abs(pct) < 0.5) return <span className="text-xs text-zinc-400">sin cambio</span>;
      up = pct > 0;
      text = `${up ? "+" : ""}${Math.round(pct)}%`;
    }
  }
  const Icon = up ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        up ? "text-emerald-600" : "text-red-600"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {text}
    </span>
  );
};

// Tarjeta KPI: label + valor + delta vs periodo anterior + sparkline opcional.
const StatTile = ({ icon: Icon, title, value, current, previous, unit, hint, spark }) => (
  <Section icon={Icon} title={title}>
    <div className="flex items-end justify-between gap-3">
      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-zinc-900">{value}</span>
          {hint && <InfoHint text={hint} />}
        </div>
        <div className="mt-1 h-4">
          <DeltaChip current={current} previous={previous} unit={unit} />
        </div>
      </div>
      {spark && spark.length > 1 && (
        <div className="w-24 h-10 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={spark} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <Line
                type="monotone"
                dataKey="v"
                stroke={ACCENT}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  </Section>
);

// Embudo horizontal con divs: anchos proporcionales a la etapa mayor,
// data-end redondeado 4px, valor y % fuera de la barra (nunca clipeado).
const FunnelRow = ({ label, value, max, color, pct }) => {
  const width = max > 0 ? Math.max((value / max) * 100, value > 0 ? 2 : 0) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 shrink-0 text-sm text-zinc-600">{label}</div>
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <div className="h-6 rounded-r bg-zinc-100 relative overflow-hidden" style={{ width: "100%" }}>
          <div
            className="h-full rounded-r"
            style={{ width: `${width}%`, backgroundColor: color }}
          />
        </div>
      </div>
      <div className="w-32 shrink-0 text-right">
        <span className="text-sm font-semibold text-zinc-900 tabular-nums">{value}</span>
        {pct != null && (
          <span className="ml-1.5 text-xs text-zinc-400 tabular-nums">({fmtPct(pct)})</span>
        )}
      </div>
    </div>
  );
};

const ChartToggle = ({ options, value, onChange }) => (
  <div className="flex gap-1">
    {options.map(({ key, icon: Icon, label }) => (
      <button
        key={key}
        type="button"
        aria-label={label}
        className={`p-1.5 rounded-lg transition ${
          value === key ? "bg-zinc-100" : "hover:bg-zinc-50"
        }`}
        onClick={() => onChange(key)}
      >
        <Icon className={`h-4 w-4 ${value === key ? "text-zinc-900" : "text-zinc-400"}`} />
      </button>
    ))}
  </div>
);

const EmptyNote = ({ text }) => (
  <div className="flex items-center justify-center h-40 text-sm text-zinc-400">{text}</div>
);

const AnaliticasWeb = () => {
  const { loads, socialClicks, contactForms } = useVenue();
  const [timeframe, setTimeframe] = useState(30);
  const [visitsView, setVisitsView] = useState("bar"); // bar | line | table
  const [socialView, setSocialView] = useState("chart"); // chart | table

  const now = Date.now();
  const isAll = timeframe === "all";
  const cutoff = isAll ? -Infinity : now - timeframe * DAY_MS;
  const prevCutoff = isAll ? -Infinity : now - 2 * timeframe * DAY_MS;

  // Un solo pase por colección: separa periodo actual y periodo anterior
  // (mismo largo) para las variaciones de los KPIs.
  const data = useMemo(() => {
    const inWindow = (items, tsOf) => {
      const cur = [];
      const prev = [];
      items.forEach((item) => {
        const ms = tsOf(item);
        if (ms == null) return;
        if (ms >= cutoff) cur.push({ ...item, _ms: ms });
        else if (!isAll && ms >= prevCutoff) prev.push({ ...item, _ms: ms });
      });
      return { cur, prev };
    };
    const visits = inWindow(loads || [], (l) => toMs(l.timestamp));
    const clicks = inWindow(socialClicks || [], (c) => toMs(c.timestamp));
    const webForms = inWindow(
      (contactForms || []).filter((f) => isContactFormLead(f.origin)),
      (f) => toMs(f.createdAt)
    );
    return { visits, clicks, webForms };
  }, [loads, socialClicks, contactForms, cutoff, prevCutoff, isAll]);

  // Serie temporal continua (rellena días sin visitas con 0 — sin huecos
  // engañosos). En "Todo" agrupa por mes para no saturar el eje.
  const buckets = useMemo(() => {
    const keyOf = isAll ? monthKey : dayKey;
    const labelOf = isAll ? monthLabel : dayLabel;
    const keys = [];
    if (isAll) {
      const allMs = [
        ...data.visits.cur.map((v) => v._ms),
        ...data.clicks.cur.map((c) => c._ms),
        ...data.webForms.cur.map((f) => f._ms),
      ];
      if (allMs.length === 0) return [];
      const start = new Date(Math.min(...allMs));
      const startDate = new Date(start.getFullYear(), start.getMonth(), 15);
      const end = new Date();
      for (let d = startDate; d <= end; d = new Date(d.getFullYear(), d.getMonth() + 1, 15)) {
        keys.push(monthKey(d.getTime()));
      }
    } else {
      for (let i = timeframe - 1; i >= 0; i--) {
        keys.push(dayKey(now - i * DAY_MS));
      }
    }
    const base = new Map(
      keys.map((k) => [k, { key: k, label: labelOf(k), visitas: 0, ig: 0, wa: 0, web: 0, otro: 0, formularios: 0 }])
    );
    data.visits.cur.forEach((v) => {
      const b = base.get(keyOf(v._ms));
      if (b) b.visitas += 1;
    });
    data.clicks.cur.forEach((c) => {
      const b = base.get(keyOf(c._ms));
      if (!b) return;
      const p = PLATFORM_META[c.platform] ? c.platform : "otro";
      b[p] += 1;
    });
    data.webForms.cur.forEach((f) => {
      const b = base.get(keyOf(f._ms));
      if (b) b.formularios += 1;
    });
    return Array.from(base.values());
  }, [data, isAll, timeframe, now]);

  const stats = useMemo(() => {
    const uniq = (arr) => new Set(arr.map((l) => l.ip)).size;
    const visitas = data.visits.cur.length;
    const visitasPrev = isAll ? null : data.visits.prev.length;
    const formularios = data.webForms.cur.length;
    const formulariosPrev = isAll ? null : data.webForms.prev.length;
    const clicksTotal = data.clicks.cur.length;
    // Contacto = formularios web + clics a redes: un clic a WhatsApp/IG es
    // una conversación que sigue fuera del sitio (definición del usuario,
    // misma fórmula en AdminStats).
    const rate = visitas > 0 ? ((formularios + clicksTotal) / visitas) * 100 : null;
    const ratePrev =
      !isAll && data.visits.prev.length > 0
        ? ((data.webForms.prev.length + data.clicks.prev.length) / data.visits.prev.length) * 100
        : null;
    const mobile = data.visits.cur.filter((l) => l.is_mobile).length;
    const byPlatform = {};
    data.clicks.cur.forEach((c) => {
      const p = PLATFORM_META[c.platform] ? c.platform : "otro";
      byPlatform[p] = (byPlatform[p] || 0) + 1;
    });
    return {
      visitas,
      visitasPrev,
      unicos: uniq(data.visits.cur),
      unicosPrev: isAll ? null : uniq(data.visits.prev),
      formularios,
      formulariosPrev,
      clicksTotal,
      rate,
      ratePrev,
      mobile,
      desktop: visitas - mobile,
      byPlatform,
    };
  }, [data, isAll]);

  const sparkVisitas = buckets.map((b) => ({ v: b.visitas }));
  const sparkForms = buckets.map((b) => ({ v: b.formularios }));
  const activePlatforms = PLATFORM_ORDER.filter((p) => (stats.byPlatform[p] || 0) > 0);
  const maxPlatform = Math.max(1, ...activePlatforms.map((p) => stats.byPlatform[p]));
  const mobilePct = stats.visitas > 0 ? (stats.mobile / stats.visitas) * 100 : 0;
  const periodNoun = isAll ? null : `${timeframe} días previos`;

  return (
    <>
      {/* Filtro de periodo — una sola fila que gobierna TODO lo de abajo */}
      <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-zinc-500 mr-1">Periodo:</span>
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.value}
              type="button"
              onClick={() => setTimeframe(tf.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                timeframe === tf.value
                  ? "text-white shadow-sm"
                  : "text-zinc-500 bg-zinc-100 hover:bg-zinc-200"
              }`}
              style={timeframe === tf.value ? { backgroundColor: ACCENT } : undefined}
            >
              {tf.label}
            </button>
          ))}
          {periodNoun && (
            <span className="ml-auto text-xs text-zinc-400">
              Variaciones vs. {periodNoun}
            </span>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatTile
          icon={GlobeAltIcon}
          title="Visitas"
          value={stats.visitas}
          current={stats.visitas}
          previous={stats.visitasPrev}
          spark={sparkVisitas}
        />
        <StatTile
          icon={GlobeAltIcon}
          title="Visitantes únicos"
          value={stats.unicos}
          current={stats.unicos}
          previous={stats.unicosPrev}
          hint="Direcciones IP distintas que visitaron tu página en el periodo."
        />
        <StatTile
          icon={EnvelopeIcon}
          title="Formularios"
          value={stats.formularios}
          current={stats.formularios}
          previous={stats.formulariosPrev}
          hint="Formularios de contacto que llegaron desde tu página de Venueboard (no incluye leads capturados a mano)."
          spark={sparkForms}
        />
        <StatTile
          icon={FunnelIcon}
          title="% de contacto"
          value={stats.rate == null ? "—" : fmtPct(stats.rate)}
          current={stats.rate}
          previous={stats.ratePrev}
          unit="pp"
          hint="Formularios más clics a redes (WhatsApp, Instagram, sitio web) entre visitas del periodo: qué porcentaje de las visitas terminó en un contacto."
        />
      </div>

      {/* Visitas por día + embudo */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Section
          icon={ChartBarIcon}
          title={isAll ? "Visitas por mes" : "Visitas por día"}
          className="lg:col-span-2"
          action={
            <ChartToggle
              value={visitsView}
              onChange={setVisitsView}
              options={[
                { key: "bar", icon: ChartBarIcon, label: "Barras" },
                { key: "line", icon: PresentationChartLineIcon, label: "Línea" },
                { key: "table", icon: TableCellsIcon, label: "Tabla" },
              ]}
            />
          }
        >
          {buckets.length === 0 || stats.visitas === 0 ? (
            <EmptyNote text="Sin visitas en este periodo." />
          ) : visitsView === "table" ? (
            <div className="max-h-64 overflow-y-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>{isAll ? "Mes" : "Día"}</TableHeader>
                    <TableHeader>Visitas</TableHeader>
                    <TableHeader>Formularios</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...buckets].reverse().map((b) => (
                    <TableRow key={b.key}>
                      <TableCell>{b.label}</TableCell>
                      <TableCell className="tabular-nums">{b.visitas}</TableCell>
                      <TableCell className="tabular-nums">{b.formularios}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              {visitsView === "bar" ? (
                <BarChart data={buckets} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke="#f4f4f5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} minTickGap={18} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f4f4f5" }} />
                  <Bar dataKey="visitas" name="Visitas" fill={ACCENT} radius={[4, 4, 0, 0]} maxBarSize={24} />
                </BarChart>
              ) : (
                <LineChart data={buckets} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke="#f4f4f5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} minTickGap={18} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="visitas" name="Visitas" stroke={ACCENT} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: "#ffffff" }} />
                </LineChart>
              )}
            </ResponsiveContainer>
          )}
        </Section>

        <Section icon={FunnelIcon} title="Embudo de contacto">
          {stats.visitas === 0 ? (
            <EmptyNote text="Sin visitas en este periodo." />
          ) : (
            <div className="space-y-4 pt-2">
              <FunnelRow
                label="Visitas"
                value={stats.visitas}
                max={stats.visitas}
                color={FUNNEL_COLORS[0]}
              />
              <FunnelRow
                label="Clics sociales"
                value={stats.clicksTotal}
                max={stats.visitas}
                color={FUNNEL_COLORS[1]}
                pct={(stats.clicksTotal / stats.visitas) * 100}
              />
              <FunnelRow
                label="Formularios"
                value={stats.formularios}
                max={stats.visitas}
                color={FUNNEL_COLORS[2]}
                pct={(stats.formularios / stats.visitas) * 100}
              />
              <p className="text-xs text-zinc-400 pt-1">
                De cada visita a tu página: cuántas hicieron clic a tus redes y
                cuántas te escribieron. El porcentaje es sobre las visitas.
              </p>
            </div>
          )}
        </Section>
      </div>

      {/* Dispositivo + redes sociales */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section icon={DevicePhoneMobileIcon} title="Dispositivo">
          {stats.visitas === 0 ? (
            <EmptyNote text="Sin visitas en este periodo." />
          ) : (
            <div className="pt-2">
              {/* Barra dividida (2 segmentos con gap de superficie) — un pie
                  de 2 rebanadas es un anti-patrón; esto se lee de un vistazo */}
              <div className="flex h-8 w-full overflow-hidden rounded-lg" style={{ gap: 2 }}>
                <div
                  className="h-full rounded-l-lg"
                  style={{ width: `${Math.max(mobilePct, 2)}%`, backgroundColor: ACCENT }}
                />
                <div
                  className="h-full flex-1 rounded-r-lg"
                  style={{ backgroundColor: "#d4e3da" }}
                />
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-zinc-600">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ACCENT }} />
                    Mobile
                  </span>
                  <span className="font-semibold text-zinc-900 tabular-nums">
                    {stats.mobile}
                    <span className="ml-1.5 text-xs font-normal text-zinc-400">{fmtPct(mobilePct, 0)}</span>
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-zinc-600">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#d4e3da" }} />
                    Desktop
                  </span>
                  <span className="font-semibold text-zinc-900 tabular-nums">
                    {stats.desktop}
                    <span className="ml-1.5 text-xs font-normal text-zinc-400">{fmtPct(100 - mobilePct, 0)}</span>
                  </span>
                </div>
              </div>
            </div>
          )}
        </Section>

        <Section
          icon={CursorArrowRaysIcon}
          title="Clics a redes sociales"
          className="lg:col-span-2"
          action={
            <ChartToggle
              value={socialView}
              onChange={setSocialView}
              options={[
                { key: "chart", icon: ChartBarIcon, label: "Gráfica" },
                { key: "table", icon: TableCellsIcon, label: "Tabla" },
              ]}
            />
          }
        >
          {stats.clicksTotal === 0 ? (
            <EmptyNote text="Sin clics a redes en este periodo." />
          ) : socialView === "table" ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Plataforma</TableHeader>
                  <TableHeader>Clics</TableHeader>
                  <TableHeader>% del total</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {activePlatforms.map((p) => (
                  <TableRow key={p}>
                    <TableCell>{PLATFORM_META[p].label}</TableCell>
                    <TableCell className="tabular-nums">{stats.byPlatform[p]}</TableCell>
                    <TableCell className="tabular-nums">
                      {fmtPct((stats.byPlatform[p] / stats.clicksTotal) * 100, 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-start">
              {/* Totales por plataforma: barras horizontales con etiqueta
                  directa (el valor visible es el relevo de contraste del rosa) */}
              <div className="md:col-span-2 space-y-3 pt-1">
                {activePlatforms.map((p) => (
                  <div key={p} className="flex items-center gap-3">
                    <span className="w-20 shrink-0 text-sm text-zinc-600">
                      {PLATFORM_META[p].label}
                    </span>
                    <div className="flex-1 h-5 bg-zinc-100 rounded-r overflow-hidden">
                      <div
                        className="h-full rounded-r"
                        style={{
                          width: `${(stats.byPlatform[p] / maxPlatform) * 100}%`,
                          backgroundColor: PLATFORM_META[p].color,
                        }}
                      />
                    </div>
                    <span className="w-8 shrink-0 text-right text-sm font-semibold text-zinc-900 tabular-nums">
                      {stats.byPlatform[p]}
                    </span>
                  </div>
                ))}
                <p className="text-xs text-zinc-400 pt-1">
                  {stats.clicksTotal} clic{stats.clicksTotal === 1 ? "" : "s"} en total
                </p>
              </div>
              {/* Por día, apiladas — mismo color por plataforma en toda la
                  sección (el color sigue a la entidad) */}
              <div className="md:col-span-3">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={buckets} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid stroke="#f4f4f5" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} minTickGap={18} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f4f4f5" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                    {PLATFORM_ORDER.filter((p) => activePlatforms.includes(p)).map((p) => (
                      <Bar
                        key={p}
                        dataKey={p}
                        name={PLATFORM_META[p].label}
                        stackId="social"
                        fill={PLATFORM_META[p].color}
                        stroke="#ffffff"
                        strokeWidth={1}
                        maxBarSize={24}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </Section>
      </div>
    </>
  );
};

export default AnaliticasWeb;
